import React from 'react';
import { Loader, Icon } from '@scuf/common';
import { ErrorGeneric } from './useFetch';

interface IViewDataProps {
  /** Should be above 120px in order to not squish/overlap the loading icon */
  minHeight?: string;

  /** Any custom JSX to go in place of the icon in the default error segment */
  errorIcon?: () => JSX.Element;

  /** Error message to replace the default error segment text: "Could not fetch data" */
  errorMessage?: string;

  /** Loading message to replace the default loading text: "Loading..." */
  loadingMessage?: string;

  /** Used to override the error segment with custom JSX */
  errorRender?: () => JSX.Element;

  /** Used to override the loading segment with custom JSX */
  loadingRender?: () => JSX.Element;

  /** Show the error segment. Takes priority over content and loading states */
  isError: boolean | ErrorGeneric | undefined;

  /** Show the loading segment if error isnt true. Takes priority over content */
  isLoading: boolean;

  /** Renders the content with the data, if the data is available */
  contentRender: () => JSX.Element;
}

/**
 * Component to show an error segment if there should be one, a loading segment if no error present, and a content segment if neither error nor loading.
 * Best used in conjunction with our useFetch custome state hook
 */
export class ViewData extends React.Component<IViewDataProps> {
  /**
   * Decides whether or not to render default or custom view for error segment
   */
  errorView = () => {
    return this.props.errorRender
      ? this.props.errorRender()
      : this.defaultErrorView();
  };

  /**
   * The default error screen, displayed if errorRender doesnt override it
   */
  defaultErrorView = () => {
    // This needs HUE input
    return (
      <div
        style={{
          textAlign: 'center',
          minHeight: this.props.minHeight ? this.props.minHeight : '120px'
        }}
      >
        {this.props.errorIcon ? (
          this.props.errorIcon()
        ) : (
          <Icon name="badge-warning" size="medium" />
        )}
        <div>
          {this.props.errorMessage
            ? this.props.errorMessage
            : 'Could not fetch data'}
        </div>
      </div>
    );
  };

  /**
   * Decides whether or not to render default or custom view for loading segment
   */
  loadingView = () => {
    return this.props.loadingRender
      ? this.props.loadingRender()
      : this.defaultLoadingView();
  };

  /**
   * The default error screen, displayed if loadingRender doesnt override it
   */
  defaultLoadingView = () => {
    return (
      <div style={{ position: 'relative' }}>
        <Loader
          minHeight={this.props.minHeight ? this.props.minHeight : '120px'}
          text={
            this.props.loadingMessage ? this.props.loadingMessage : undefined
          }
        />
      </div>
    );
  };

  render() {
    return (
      <>
        {this.props.isError
          ? this.errorView()
          : this.props.isLoading
          ? this.loadingView()
          : this.props.contentRender()}
      </>
    );
  }
}

export default ViewData;
